import React, { useMemo } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit3, Building2, Target, Shield, TrendingUp, Globe, Award, Paperclip, Compass, Lightbulb } from "lucide-react";
import { formatGrantHistoryList } from "@/lib/grantHistory";
import { getGroupCompanyLabel } from "./groupCompanyDisplay";

const PROJECT_MATURITY_LABELS = {
  poc: "Proof of concept (POC)",
  "proof of concept (poc)": "Proof of concept (POC)",
  pov: "Proof of value (POV)",
  "proof of value (pov)": "Proof of value (POV)",
  implementation: "Implementation / scale-up",
  "implementation / scale-up": "Implementation / scale-up",
  unspecified: "Not specified",
  "not specified": "Not specified",
};

function formatProjectMaturity(value) {
  if (!value || typeof value !== "string") return null;
  const key = value.trim().toLowerCase();
  return PROJECT_MATURITY_LABELS[key] || value;
}

function formatFundingSummaryValue(value) {
  if (!value || typeof value !== "string") return "Not specified";
  return value
    .split("_")
    .join(" ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

/** Section anchors rendered by each step (`id="step-…"`), so Edit can land on the right section. */
// eslint-disable-next-line react-refresh/only-export-components
export const REVIEW_SECTION_IDS = {
  company: "step-company",
  metrics: "step-metrics",
  ops: "step-ops",
  history: "step-history",
  funding: "step-funding-strategy",
  project: "step-project",
  compliance: "step-compliance",
  supportingDocs: "step-supporting-documents",
};

export default function ReviewStep({
  formData,
  onEdit,
  supportingDocsStepEnabled = false,
  fundingStepNumber = 5,
  projectStepNumber = 6,
  complianceStepNumber = 7,
  supportingDocsStepNumber = 8,
}) {
  const grantHistoryBadges = useMemo(
    () => formatGrantHistoryList(formData.prior_apps || []),
    [formData.prior_apps]
  );

  const supportingDocCount = Array.isArray(formData.supporting_documents)
    ? formData.supporting_documents.length
    : 0;

  const TEXT_BASE = "#4B5563";
  const editButtonClasses =
    "flex items-center gap-1 text-[#60A5FA] border border-[rgba(96,165,250,0.35)] bg-white rounded-full px-4 py-2 transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 focus-visible:outline-none";
  const sectionTitleStyle = { color: "#60A5FA" };
  const labelStyle = { color: "#94A3B8" };
  const valueStyle = { color: "#4B5563" };
  const badgeStyle = {
    border: "1px solid rgba(96, 165, 250, 0.5)",
    color: "#60A5FA",
    backgroundColor: "white",
  };

  return (
    <div id="step-review" className="space-y-8">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold mb-2" style={{ color: '#4B5563' }} tabIndex={-1} data-step-heading>Review your answers</h2>
        <p className="max-w-xl mx-auto text-sm md:text-base" style={{ color: '#4B5563' }}>
          Check each section below. Use <span className="font-semibold">Edit</span> to change any answer before you run the assessment.
        </p>
      </div>

      <div className="space-y-6">
        {/* Supporting documents — first so upload status is obvious without the sidebar */}
        {supportingDocsStepEnabled && (
          <Card className="p-6 bg-white border border-[rgba(96,165,250,0.35)] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="flex items-center gap-3">
                <Paperclip className="w-5 h-5 shrink-0" style={{ color: '#60A5FA' }} />
                <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Supporting documents</h3>
              </div>
              <Button variant="outline" size="sm" onClick={() => onEdit(supportingDocsStepNumber, REVIEW_SECTION_IDS.supportingDocs)} className={editButtonClasses} aria-label="Edit supporting documents">
                <Edit3 className="w-3 h-3" />
                Edit
              </Button>
            </div>

            <div
              className="rounded-2xl border px-4 py-3 mb-4"
              style={{
                borderColor:
                  supportingDocCount > 0 ? 'rgba(96, 165, 250, 0.5)' : 'rgba(148, 163, 184, 0.45)',
                backgroundColor:
                  supportingDocCount > 0 ? 'rgba(239, 246, 255, 0.92)' : 'rgba(248, 250, 252, 0.9)',
              }}
            >
              {supportingDocCount > 0 ? (
                <>
                  <p className="text-xs font-medium uppercase tracking-wide mb-1" style={labelStyle}>
                    Documents uploaded
                  </p>
                  <p className="text-lg font-semibold" style={{ color: '#60A5FA' }}>
                    Yes — {supportingDocCount} file{supportingDocCount === 1 ? '' : 's'}
                  </p>
                  <p className="text-sm mt-1.5 leading-normal" style={{ color: TEXT_BASE }}>
                    How these files are processed is described in our{' '}
                    <span className="font-semibold text-[#60A5FA]">Privacy Policy</span>.
                  </p>
                </>
              ) : (
                <p className="text-sm leading-normal" style={{ color: TEXT_BASE }}>
                  You can add files via Edit or submit without them. How uploads are used is described in our{' '}
                  <span className="font-semibold text-[#60A5FA]">Privacy Policy</span>.
                </p>
              )}
            </div>

            {supportingDocCount > 0 ? (
              <>
                <ul className="space-y-1 text-sm list-disc list-inside" style={{ color: TEXT_BASE }}>
                  {(formData.supporting_documents || []).map((d) => (
                    <li key={d.document_id || d.clientId}>{d.original_filename}</li>
                  ))}
                </ul>
                <p className="text-sm mt-2" style={{ color: labelStyle }}>
                  Filenames listed here are for your confirmation only.
                </p>
              </>
            ) : null}
          </Card>
        )}

        {/* Company Details */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Building2 className="w-5 h-5" style={{ color: '#60A5FA' }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Company Details</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(1, REVIEW_SECTION_IDS.company)} className={editButtonClasses} aria-label="Edit company details">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-sm" style={labelStyle}>Business Name</p>
              <p className="font-semibold" style={valueStyle}>{formData.companyName || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">UEN</p>
              <p className="font-semibold text-[#4B5563]">{formData.uen || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Incorporation Year</p>
              <p className="font-semibold text-[#4B5563]">{formData.incorporation_year || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Primary Industry</p>
              <p className="font-semibold text-[#4B5563]">{formData.industry || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Entity Type</p>
              <p className="font-semibold text-[#4B5563]">{formData.entity_type || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Group / Parent Company</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.is_group_company
                  ? `${getGroupCompanyLabel(formData.is_group_company)}${
                      formData.is_group_company === 'yes' && formData.group_relationship
                        ? ` (${formData.group_relationship})`
                        : ''
                    }`
                  : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Parent / Group Name</p>
              <p className="font-semibold text-[#4B5563]">{formData.parent_company_name || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Latest FY End</p>
              <p className="font-semibold text-[#4B5563]">{formData.latest_fy_end_date || 'Not specified'}</p>
            </div>
          </div>
        </Card>

        {/* Business Metrics */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-5 h-5" style={{ color: '#60A5FA' }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Business Metrics</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(2, REVIEW_SECTION_IDS.metrics)} className={editButtonClasses} aria-label="Edit business metrics">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[#94A3B8]">Employees</p>
              <p className="font-semibold text-[#4B5563]">{formData.employees || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Annual Revenue</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.annual_revenue_sgd ? `S$${parseFloat(formData.annual_revenue_sgd).toLocaleString()}` : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Local Ownership</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.local_ownership_pct ? `${formData.local_ownership_pct}%` : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Business Stage</p>
              <p className="font-semibold text-[#4B5563]">{formData.business_stage || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Financial Viability</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.financial_viability === true ? 'Yes, financially viable' : 
                 formData.financial_viability === false ? 'No, not financially viable' : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Cash Runway</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.cash_runway_band || 'Not specified'}
              </p>
            </div>
          </div>
        </Card>

        {/* Location & Operations */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Globe className="w-5 h-5" style={{ color: '#60A5FA' }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Location & Operations</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(3, REVIEW_SECTION_IDS.ops)} className={editButtonClasses} aria-label="Edit location and operations">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-[#94A3B8] text-sm mb-2">Operational Presence ({formData.operational_presence?.length || 0})</p>
              <div className="flex flex-wrap gap-2">
                {formData.operational_presence?.map((presence) => (
                  <Badge key={presence} variant="secondary" className="text-xs px-3 py-1 rounded-full border" style={badgeStyle}>
                    {presence}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Grant History */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Award className="w-5 h-5" style={{ color: '#60A5FA' }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Grant History</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(4, REVIEW_SECTION_IDS.history)} className={editButtonClasses} aria-label="Edit grant history">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>
          <div className="space-y-4">
            <div>
              <p className="text-[#94A3B8] text-sm mb-2">Previous Grant Applications ({grantHistoryBadges.length})</p>
              <div className="flex flex-wrap gap-2">
                {grantHistoryBadges.map((grant) => (
                  <Badge key={grant} variant="secondary" className="text-xs px-3 py-1 rounded-full border" style={badgeStyle}>
                    {grant}
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </Card>

        {/* Funding Strategy */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
          <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
              <Compass className="w-5 h-5" style={{ color: "#60A5FA" }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Funding Strategy</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(fundingStepNumber, REVIEW_SECTION_IDS.funding)} className={editButtonClasses} aria-label="Edit funding strategy">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[#94A3B8]">Company category</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.eligible_entity_type)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Funding Goal</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.funding_goal)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Capital Preference</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.funding_preference)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Revenue Status</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.revenue_status)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Business Model</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.business_model)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Deployment Environment</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.deployment_environment)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Primary Use Of Funds</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.primary_use_of_funds)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Target Buyer / Customer</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.target_customer_type)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Current Traction</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.traction_stage)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Market Scope</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.market_scope)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Application Readiness</p>
              <p className="font-semibold text-[#4B5563]">{formatFundingSummaryValue(formData.application_readiness)}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">VC-backed</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.vc_backed === true ? "Yes" : formData.vc_backed === false ? "No" : "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Pilot-ready</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.pilot_ready === true ? "Yes" : formData.pilot_ready === false ? "No" : "Not specified"}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Impact Intent</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.impact_intent === true ? "Impact-driven" : formData.impact_intent === false ? "Commercial-first" : "Not specified"}
              </p>
            </div>
          </div>
          {formData.impact_intent === true ? (
            <div className="mt-4 rounded-2xl border px-4 py-3" style={{ borderColor: "rgba(96, 165, 250, 0.35)", backgroundColor: "rgba(239, 246, 255, 0.75)" }}>
              <div className="flex items-center gap-2 text-sm font-medium" style={{ color: "#60A5FA" }}>
                <Lightbulb className="w-4 h-4" />
                Impact funding pathways will also be tested
              </div>
            </div>
          ) : null}
        </Card>

        {/* Project Details */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl" style={{ color: TEXT_BASE }}>
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Target className="w-5 h-5" style={{ color: '#60A5FA' }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Project Details</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(projectStepNumber, REVIEW_SECTION_IDS.project)} className={editButtonClasses} aria-label="Edit project details">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-[#94A3B8]">Project Type</p>
              <p className="font-semibold text-[#4B5563]">{formData.project_type || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Project stage</p>
              <p className="font-semibold text-[#4B5563]">
                {formatProjectMaturity(formData.project_maturity) || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Timeline</p>
              <p className="font-semibold text-[#4B5563]">{formData.expected_timeline || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Budget</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.project_budget_sgd ? `S$${parseFloat(formData.project_budget_sgd).toLocaleString()}` : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Co-funding Capacity</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.cofunding_capacity !== null && formData.cofunding_capacity !== undefined ? `${formData.cofunding_capacity}%` : 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Business Function Impacted</p>
              <p className="font-semibold text-[#4B5563]">{formData.business_function_impacted || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Vendor Selected</p>
              <p className="font-semibold text-[#4B5563]">
                {formData.vendor_selected || 'Not specified'}
              </p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Vendor Location</p>
              <p className="font-semibold text-[#4B5563]">{formData.vendor_country || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Vendor Related Party</p>
              <p className="font-semibold text-[#4B5563]">{formData.vendor_related_party || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Quotations</p>
              <p className="font-semibold text-[#4B5563]">{formData.quotation_count || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Funding Upfront</p>
              <p className="font-semibold text-[#4B5563]">{formData.can_fund_upfront || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Application Priority</p>
              <p className="font-semibold text-[#4B5563]">{formData.application_priority || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-[#94A3B8]">Scope Flexibility</p>
              <p className="font-semibold text-[#4B5563]">{formData.scope_flexibility || 'Not specified'}</p>
            </div>
          </div>
          {formData.project_objectives?.length > 0 && (
            <div className="mt-4">
              <p className="text-[#94A3B8] text-sm mb-2">Objectives</p>
              <div className="flex flex-wrap gap-2">
                {formData.project_objectives.map((objective) => (
                  <Badge key={objective} variant="secondary" className="text-xs px-3 py-1 rounded-full border" style={badgeStyle}>
                    {objective}
                  </Badge>
                ))}
              </div>
            </div>
          )}
          {formData.expected_outcomes && (
            <div className="mt-4">
              <p className="text-[#94A3B8] text-sm">Expected Outcomes</p>
              <p className="font-semibold text-sm" style={{ color: TEXT_BASE }}>
                {formData.expected_outcomes}
              </p>
            </div>
          )}
          {(formData.target_market || formData.existing_target_market_presence) && (
            <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[#94A3B8]">Target Overseas Market</p>
                <p className="font-semibold text-[#4B5563]">{formData.target_market || 'Not specified'}</p>
              </div>
              <div>
                <p className="text-[#94A3B8]">Existing Presence</p>
                <p className="font-semibold text-[#4B5563]">{formData.existing_target_market_presence || 'Not specified'}</p>
              </div>
            </div>
          )}
          {formData.expansion_activity_types?.length > 0 && (
            <div className="mt-4">
              <p className="text-[#94A3B8] text-sm mb-2">Overseas Expansion Activities</p>
              <div className="flex flex-wrap gap-2">
                {formData.expansion_activity_types.map((activity) => (
                  <Badge key={activity} variant="secondary" className="text-xs px-3 py-1 rounded-full border" style={badgeStyle}>
                    {activity}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </Card>

        {/* Compliance */}
        <Card className="p-6 bg-white border border-[#E5E7EB] shadow-xl rounded-3xl">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5" style={{ color: '#60A5FA' }} />
              <h3 className="text-lg font-semibold" style={sectionTitleStyle}>Compliance & Documents</h3>
            </div>
            <Button variant="outline" size="sm" onClick={() => onEdit(complianceStepNumber, REVIEW_SECTION_IDS.compliance)} className={editButtonClasses} aria-label="Edit compliance and documents">
              <Edit3 className="w-3 h-3" />
              Edit
            </Button>
          </div>

          <div className="space-y-4">
            <div>
              <p className="text-[#94A3B8] text-sm mb-2">Available Documents ({formData.available_documents?.length || 0})</p>
              <div className="flex flex-wrap gap-2">
                {formData.available_documents?.map((doc) => (
                  <Badge key={doc} variant="secondary" className="text-xs px-3 py-1 rounded-full border" style={badgeStyle}>
                    {doc}
                  </Badge>
                ))}
              </div>
            </div>

            <div className="text-sm">
              <p className="text-[#94A3B8] mb-2">Compliance Status</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.no_outstanding_issues === true ? 'bg-green-500' : 
                    formData.declarations?.no_outstanding_issues === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>No outstanding issues: {
                    formData.declarations?.no_outstanding_issues === true ? 'Yes' : 
                    formData.declarations?.no_outstanding_issues === false ? 'No' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.meets_local_shareholding === true ? 'bg-green-500' : 
                    formData.declarations?.meets_local_shareholding === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Meets local ownership: {
                    formData.declarations?.meets_local_shareholding === true ? 'Yes' : 
                    formData.declarations?.meets_local_shareholding === false ? 'No' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.authorized_to_apply === true ? 'bg-green-500' : 
                    formData.declarations?.authorized_to_apply === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Authorized to apply: {
                    formData.declarations?.authorized_to_apply === true ? 'Yes' : 
                    formData.declarations?.authorized_to_apply === false ? 'No' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.cpf_compliance_ok === true ? 'bg-green-500' : 
                    formData.declarations?.cpf_compliance_ok === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>CPF issues: {
                    formData.declarations?.cpf_compliance_ok === true ? 'None' : 
                    formData.declarations?.cpf_compliance_ok === false ? 'Outstanding' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.tax_compliance_ok === true ? 'bg-green-500' : 
                    formData.declarations?.tax_compliance_ok === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Tax / IRAS: {
                    formData.declarations?.tax_compliance_ok === true ? 'Clear' : 
                    formData.declarations?.tax_compliance_ok === false ? 'Outstanding' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.regulatory_compliance_ok === true ? 'bg-green-500' : 
                    formData.declarations?.regulatory_compliance_ok === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Regulatory issues: {
                    formData.declarations?.regulatory_compliance_ok === true ? 'None' : 
                    formData.declarations?.regulatory_compliance_ok === false ? 'Ongoing' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.prior_grant_breach === false ? 'bg-green-500' : 
                    formData.declarations?.prior_grant_breach === true ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Prior grant breaches: {
                    formData.declarations?.prior_grant_breach === false ? 'None' : 
                    formData.declarations?.prior_grant_breach === true ? 'Yes' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.project_not_commenced === true ? 'bg-green-500' : 
                    formData.declarations?.project_not_commenced === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Project commenced: {
                    formData.declarations?.project_not_commenced === true ? 'Not yet' : 
                    formData.declarations?.project_not_commenced === false ? 'Already started' : 'Not specified'
                  }</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${
                    formData.declarations?.no_debarment_or_insolvency === true ? 'bg-green-500' : 
                    formData.declarations?.no_debarment_or_insolvency === false ? 'bg-red-500' : 'bg-gray-400'
                  }`} />
                  <span style={{ color: TEXT_BASE }}>Debarment / insolvency: {
                    formData.declarations?.no_debarment_or_insolvency === true ? 'None' : 
                    formData.declarations?.no_debarment_or_insolvency === false ? 'History present' : 'Not specified'
                  }</span>
                </div>
              </div>
              {formData.financials_recency && (
                <p className="mt-2" style={{ color: TEXT_BASE }}>
                  Financials recency: {formData.financials_recency}
                </p>
              )}
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
